import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface PageBreadcrumbContextValue {
  extension: string | null;
  setExtension: (value: string | null) => void;
}

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue | null>(null);

export function PageBreadcrumbProvider({ children }: { children: ReactNode }) {
  const [extension, setExtension] = useState<string | null>(null);
  const value = useMemo(() => ({ extension, setExtension }), [extension]);
  return (
    <PageBreadcrumbContext.Provider value={value}>{children}</PageBreadcrumbContext.Provider>
  );
}

export function usePageBreadcrumbExtension(): string | null {
  const ctx = useContext(PageBreadcrumbContext);
  return ctx?.extension ?? null;
}

/**
 * Set an extra segment to display next to the current page title in the
 * admin header (e.g. an account name on the account detail page).
 * Automatically clears on unmount.
 */
export function useSetPageBreadcrumb(value: string | null | undefined): void {
  const ctx = useContext(PageBreadcrumbContext);
  const setExtension = ctx?.setExtension;
  const stable = useCallback(
    (next: string | null) => {
      setExtension?.(next);
    },
    [setExtension],
  );

  useEffect(() => {
    stable(value ?? null);
    return () => stable(null);
  }, [stable, value]);
}
